import ApiService from '../api.service'
import type { AxiosError } from 'axios'
import type { Job } from '@/types/model'

interface ErrorResponseData {
  message?: string
}

class ResponseError extends Error {
  errorCode: number
  errorMessage: string

  constructor(errorCode: number, message: string = '') {
    super(message)
    this.name = 'ResponseError'
    this.message = message
    this.errorCode = errorCode
    this.errorMessage = message
  }
}

const JobService = {
  // job
  job: async function (params: string) {
    try {
      return ApiService.get('/api/job' + params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // job create
  jobCreate: async function (params: string = '') {
    try {
      return ApiService.get('/api/job/create' + params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // job data
  jobData: async function (id: number) {
    try {
      return ApiService.get('/api/job/' + id + '/edit')
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // job add
  jobAdd: async function (params: Job) {
    try {
      return ApiService.post('/api/job', params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // job edit
  jobEdit: async function (params: Job) {
    try {
      return ApiService.put('/api/job/' + params.id, params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // job validate
  jobValidate: async function (params: Job) {
    try {
      return ApiService.put('/api/job/validate/' + params.id, params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // job delete
  jobDelete: async function (id: number) {
    try {
      return ApiService.delete('/api/job/' + id)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // job restore
  jobRestore: async function (params: Job) {
    try {
      return ApiService.post('/api/job/' + params.id + '/restore', params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // job delete permanent
  jobDeletePermanent: async function (params: Job) {
    try {
      return ApiService.post('/api/job/' + params.id + '/delete', params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },

  // generate invoice
  jobGenerateInvoice: async function (params: Job) {
    try {
      return ApiService.put('/api/job/generate/' + params.id + '/invoice', params)
    } catch (error: unknown) {
      if (isAxiosError<ErrorResponseData>(error)) {
        const status = error.response?.status ?? 500
        const message = error.response?.data?.message ?? 'Unexpected error'
        throw new ResponseError(status, message)
      }
      throw new ResponseError(500, 'Unexpected error')
    }
  },
}

// 🔍 Helper type guard
function isAxiosError<T = unknown>(error: unknown): error is AxiosError<T> {
  return (error as AxiosError<T>).isAxiosError
}

export { JobService, ResponseError }
